import get from 'lodash.get';
import { linearRegression, linearRegressionLine } from 'simple-statistics';
import { Granularity } from '../date-manager/date-manager-constants';
import { dateManagerService } from '../date-manager/date-manager-service';
import { getNumDaysInTimePeriod } from '../date-manager/date-manager-utils';
import { TimeSliderConfig } from '../filter/filter-store';
import { getDatesInRange } from '../filter/utils';
import { mapValsDeep } from '../utilFunctions/utils';
import { DataMap, DataMapAndLabels } from './utils';

type GetActualValueForDates = (
  sDate: string,
  eDate: string
) => Promise<Record<string, any>>;
interface ForecastFuncInputs {
  timeSliderConfig: TimeSliderConfig;
  actualValue: Record<string, any>;
  forecastEndDate: string;
  getActualValueForDates: GetActualValueForDates;
}
interface HistoricalVal {
  numDays: number;
  value: number;
}
interface OvertimeChartForecastFuncInputs {
  actualDataMap: DataMap;
  timeSliderConfig: TimeSliderConfig;
  forecastEndDate: string;
  isNested: boolean;
  /* This refers to data being present in dataMap under 1 level of nesting
  Ideally this shouldn't happen ever. Need to refactor overtime chart services.
  */
}

export interface DataMapAndLabelsWithForecast extends DataMapAndLabels {
  forecastDataMap?: DataMap;
}

const getActualResults = async (
  timeSliderConfig: TimeSliderConfig,
  getActualValueForDates: GetActualValueForDates
) => {
  const dates = getDatesInRange(timeSliderConfig);

  const numActualHistoricalDatapoints = 5;

  const skipDates = Math.max(
    1,
    Math.floor(dates.length / numActualHistoricalDatapoints)
  );

  const actualEndDates = [];
  for (let i = dates.length - 1; i >= 0; i -= skipDates) {
    actualEndDates.push(dates[i]);
  }
  actualEndDates.reverse();

  const actualResults = await Promise.all(
    actualEndDates.map(async (d) => {
      return {
        numDays: getNumDaysInTimePeriod(
          dateManagerService
            .getInstance()
            .parseApiDate(timeSliderConfig.startDate),
          dateManagerService.getInstance().parseApiDate(d)
        ),
        value: await getActualValueForDates(timeSliderConfig.startDate, d),
      };
    })
  );

  return actualResults;
};

const getLinearRegressionForecast = (
  actualValues: HistoricalVal[],
  forecastPeriod: number
) => {
  const forecastFunc = linearRegressionLine(
    linearRegression(actualValues.map((v) => [v.numDays, v.value]))
  );
  const forecastValue = Math.max(forecastFunc(forecastPeriod), 0);
  return forecastValue;
};

export const getForecastResult = async ({
  timeSliderConfig,
  actualValue,
  forecastEndDate,
  getActualValueForDates,
}: ForecastFuncInputs) => {
  const actualResults = await getActualResults(
    timeSliderConfig,
    getActualValueForDates
  );
  const forecastPeriod = getNumDaysInTimePeriod(
    dateManagerService.getInstance().parseApiDate(timeSliderConfig.startDate),
    dateManagerService.getInstance().parseApiDate(forecastEndDate)
  );
  const forecastedObject = mapValsDeep(actualValue, (val, path) => {
    // not sure how mapValsDeep deals with array inputs but I don't think
    // we have any such case.
    // also, in the future we might want to implement mapValsDeep ourselves to remove
    // deepdash dependency
    const historicalVals: HistoricalVal[] = actualResults.map((r) => {
      const histVal = {
        numDays: r.numDays,
        value: Number(get(r.value, path)),
      };
      return histVal;
    });
    const forecastedValue = getLinearRegressionForecast(
      historicalVals,
      forecastPeriod
    );
    return forecastedValue;
  });
  return forecastedObject;
};

export const getOvertimeForecastDataMap = ({
  actualDataMap,
  timeSliderConfig,
  forecastEndDate,
  isNested,
}: OvertimeChartForecastFuncInputs): DataMap => {
  const { formatDateApi, parseApiDate, formatApiDateToNewFormat } =
    dateManagerService.getInstance();
  const forecastStartDate = formatDateApi(
    parseApiDate(timeSliderConfig.endDate).add(1, Granularity.DAY)
  );
  const forecastTimeSliderConfig: TimeSliderConfig = {
    ...timeSliderConfig,
    startDate: forecastStartDate,
    endDate: forecastEndDate,
  };
  const dates = getDatesInRange(timeSliderConfig);
  const { granularity } = timeSliderConfig;
  const forecastDates = getDatesInRange(forecastTimeSliderConfig);
  const forecastDataMap: DataMap = Object.fromEntries(
    forecastDates.map((d) => [formatApiDateToNewFormat(d, granularity), {}])
  );
  if (isNested) {
    /* data structure in this case looks like
      {
        Jan '21: {
          Female: {
            headcount: 5
          },
          Male: {
            headcount: 8
          }
        }
      }
      Hence I need the deep traversal below
    */
    const firstLevelKeysSet = new Set<string>([]);
    const secondLevelKeysSet = new Set<string>([]);
    Object.values(actualDataMap).forEach((objForDate: Record<string, any>) => {
      Object.entries(objForDate).forEach(([key, obj]) => {
        firstLevelKeysSet.add(key);
        Object.keys(obj).forEach((key) => secondLevelKeysSet.add(key));
      });
    });
    const firstLevelKeys = Array.from(firstLevelKeysSet);
    const secondLevelKeys = Array.from(secondLevelKeysSet);
    firstLevelKeys.forEach((key1) => {
      secondLevelKeys.forEach((key2) => {
        const getValueForDate = (date: string) => {
          return actualDataMap?.[date]?.[key1]?.[key2] ?? 0;
        };

        const actualValues = dates.map((d) => {
          const formattedDate = formatApiDateToNewFormat(d, granularity);
          const numDays = getNumDaysInTimePeriod(
            dateManagerService
              .getInstance()
              .parseApiDate(timeSliderConfig.startDate),
            dateManagerService.getInstance().parseApiDate(d)
          );
          const value = getValueForDate(formattedDate);
          return { numDays, value };
        });

        forecastDates.forEach((fD, i) => {
          const formattedDate = formatApiDateToNewFormat(fD, granularity);
          if (!forecastDataMap[formattedDate][key1]) {
            forecastDataMap[formattedDate][key1] = {};
          }
          forecastDataMap[formattedDate][key1][key2] =
            getLinearRegressionForecast(
              actualValues,
              getNumDaysInTimePeriod(
                dateManagerService
                  .getInstance()
                  .parseApiDate(timeSliderConfig.startDate),
                dateManagerService.getInstance().parseApiDate(fD)
              )
            );
        });
      });
    });
  } else {
    const keysSet = new Set<string>([]);
    Object.values(actualDataMap).forEach((objForDate: Record<string, any>) => {
      Object.keys(objForDate).forEach((key) => keysSet.add(key));
    });
    const keys = Array.from(keysSet);
    keys.forEach((key) => {
      const getValueForDate = (date: string) => {
        return actualDataMap?.[date]?.[key] ?? 0;
      };

      const actualValues = dates.map((d) => {
        const formattedDate = formatApiDateToNewFormat(d, granularity);
        const numDays = getNumDaysInTimePeriod(
          dateManagerService
            .getInstance()
            .parseApiDate(timeSliderConfig.startDate),
          dateManagerService.getInstance().parseApiDate(d)
        );
        const value = getValueForDate(formattedDate);
        return { numDays, value };
      });

      forecastDates.forEach((fD, i) => {
        const formattedDate = formatApiDateToNewFormat(fD, granularity);
        forecastDataMap[formattedDate][key] = getLinearRegressionForecast(
          actualValues,
          getNumDaysInTimePeriod(
            dateManagerService
              .getInstance()
              .parseApiDate(timeSliderConfig.startDate),
            dateManagerService.getInstance().parseApiDate(fD)
          )
        );
      });
    });
  }
  return forecastDataMap;
};
